import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

import {
  approachLabel,
  authorityLabelOf,
  decisionReasons,
  commentFields,
  recordToLinks,
  displayDate,
  workOrderDocuments,
  routingStatusLabel,
  workOrderStatusLabel,
} from "../../Modules/WorkOrder/Constants";
import { getFormattedDate, formatDocVersion, parseDocVersionNumber } from "../CommonUtils";
import { mahbLogo } from "./mahbLogo";
import { panelContractLogo } from "./panelContractLogo";
import { pdfLogo } from "./pdfLogo";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

/** What the PDF reads beyond the record itself — the details page's other tabs. */
export interface WorkOrderPdfExtras {
  comments?: any[];
  logs?: any[];
}

/**
 * The work order as a PDF, laid out the way e-POD lays out its SPA document
 * (generate-pdf.ts): centred masthead, navy section titles, navy label over a
 * black value, tables ruled under the header only, "n of N" in the corner, and
 * the record's Comments, Versions and Logs at the end. The masthead pairs the
 * MYAirports logo with the Panel Contract logo in place of e-POD's. View, Print, Download and Document Versions' generated PDF are
 * built here; Draft / Final Work Order use the letter (draftWorkOrderPdf).
 *
 * Sections, labels and their order follow the details page, which follows the
 * Add Work Order inputs. No watermark — a draft prints like any other.
 */
export const workOrderPdf = (detail: any, routing: any[], { comments = [], logs = [] }: WorkOrderPdfExtras = {}) => {
  const dash = (value: any) => (value === undefined || value === null || value === "" ? "-" : String(value));
  const num = (value: any) => (value === "" || value === null || value === undefined ? "-" : Number(value).toLocaleString());

  const field = (label: string, value: any) => ({
    stack: [
      { text: label, style: "fieldLabel" },
      /* A zero-width space after @ . / _ - lets a long email or number wrap
         inside its column instead of widening it and shifting the row. */
      { text: dash(value).replace(/([@./_-])/g, "$1\u200B"), style: "fieldValue" },
    ],
    marginBottom: 14,
  });
  /* Three to a row, as e-POD sets its short fields; long text takes the row. */
  const fields = (items: { label: string; value: any; wide?: boolean }[]) => {
    const rows: any[] = [];
    let row: any[] = [];
    const flush = () => {
      if (!row.length) return;
      while (row.length < 3) row.push({ text: "" });
      rows.push({ columns: row.map((cell) => ({ ...cell, width: "*" })), columnGap: 16 });
      row = [];
    };
    items.forEach((item) => {
      if (item.wide) {
        flush();
        rows.push(field(item.label, item.value));
        return;
      }
      row.push(field(item.label, item.value));
      if (row.length === 3) flush();
    });
    flush();
    return rows;
  };
  /* The record carries no version of its own: it is the newest of its documents'. */
  const newestDocVersion = (detail?.documentVersionResponses ?? detail?.documents ?? []).reduce(
    (best: number, doc: any) => Math.max(best, parseDocVersionNumber(doc?.docVersion)),
    0
  );
  /* Every version up to this one, newest first (V2, V1, V0), each with its own time. */
  const thisVersion = parseDocVersionNumber(detail?.docVersion ?? newestDocVersion);
  const versionRows: any[][] = ((detail?.versionHistory as any[]) ?? [])
    .filter((row) => parseDocVersionNumber(row?.docVersion) <= thisVersion)
    .sort((a, b) => parseDocVersionNumber(b.docVersion) - parseDocVersionNumber(a.docVersion))
    .filter((row, index, all) => all.findIndex((other) => parseDocVersionNumber(other.docVersion) === parseDocVersionNumber(row.docVersion)) === index)
    .map((row) => [formatDocVersion(row.docVersion), getFormattedDate(row.updatedOn ?? row.createdOn)]);
  if (!versionRows.length) {
    versionRows.push([formatDocVersion(detail?.docVersion ?? newestDocVersion), getFormattedDate(detail?.updatedOn ?? detail?.createdOn)]);
  }
  const title = (text: string) => ({ text, style: "pageTitle", marginTop: 20, marginBottom: 8, headlineLevel: 1 });
  /* e-POD's table: navy headers, one rule under the header row, nothing else. */
  const table = (headers: string[], widths: string[], rows: any[][], empty: string) => ({
    table: {
      headerRows: 1,
      widths,
      body: [
        headers.map((h) => ({ text: h, style: "tableHeader" })),
        ...(rows.length
          ? rows.map((r) => r.map((cell) => ({ text: dash(cell), style: "tableContent" })))
          : [[{ text: empty, style: "tableContent", colSpan: headers.length, alignment: "center" }, ...headers.slice(1).map(() => ({}))]]),
      ],
    },
    layout: {
      hLineWidth: (i: number) => (i === 1 ? 0.5 : 0),
      vLineWidth: () => 0,
      hLineColor: () => "grey",
    },
  });

  const sor: any[] = detail?.sorDetails ?? detail?.sorItems ?? [];
  /* v2.1 sends the total; older records do not, so it is summed from the lines. */
  const total = Number(detail?.totalAmount ?? sor.reduce((sum, row) => sum + (Number(row.amount) || 0), 0)) || 0;
  const links: any[] = recordToLinks(detail ?? {});
  const overallPdfRegex = /^(SPA|WorkOrder).*\.pdf$/i;
  const documents = workOrderDocuments(detail).filter(
    (doc: any) => !overallPdfRegex.test(doc.name ?? doc.documentName ?? "")
  );
  const isApprover = (row: any) => String(row.role ?? "").toUpperCase().includes("APPROVER");
  const reviewers = routing.filter((row) => !isApprover(row));
  const approvers = routing.filter(isApprover);
  /* Routing rows: a step that has acted reads "Approved", as on the page. */
  const status = (value: any) => (value ? routingStatusLabel(value) : "");
  const reasons = decisionReasons(detail, routing);

  return {
    pageSize: "A4",
    pageOrientation: "portrait",
    pageMargins: [20, 90, 20, 30],
    /* The MYAirports logo the work order letter carries, beside the Panel
       Contract logo — as e-POD's PDF pairs it with the e-POD logo. */
    header: () => ({
      columns: [
        { text: "", width: "*" },
        { width: 110, image: mahbLogo, fit: [110, 56] },
        { width: 120, image: panelContractLogo, fit: [120, 37], margin: [0, 10, 0, 0] },
        { text: "", width: "*" },
      ],
      columnGap: 14,
      margin: [0, 12],
    }),
    footer: (current: number, count: number) => [{ text: `${current} of ${count}`, alignment: "right", marginRight: 40 }],
    /* A section title never ends a page on its own: one starting in the last
       15% of the page moves over with the content it introduces.
       ponytail: fixed threshold, measure the following block if a long title
       or a tall first row still splits. */
    pageBreakBefore: (node: any) => node.headlineLevel === 1 && node.startPosition?.verticalRatio > 0.85,
    content: [
      { text: "Project Information", style: "pageTitle", marginBottom: 16 },
      ...fields([
        { label: "Work Order No.", value: detail?.workOrderNo },
        /* As the detail page shows it, in Project ID's place. */
        { label: "Overall Status", value: detail?.overallStatus && workOrderStatusLabel(detail.overallStatus) },
        /* Same order and labels as the Add Work Order inputs. */
        { label: "Requester Name", value: detail?.requesterName },
        ...(reasons.isRejected ? [{ label: "Reason for Rejection", value: reasons.rejectReason, wide: true }] : []),
        ...(reasons.isCancelled ? [{ label: "Reason for Cancellation", value: reasons.cancelReason, wide: true }] : []),
        { label: "Requester Email", value: detail?.requesterEmail },
        { label: "Date Request", value: displayDate(detail?.dateRequest) },
        { label: "Job Title", value: detail?.jobTitle },
        { label: "Subsidiary/Division", value: detail?.subsidiary },
        { label: "Cost Centre", value: detail?.costCentre },
        { label: "Department", value: detail?.department },
        { label: "PR No.", value: detail?.prNo },
        { label: "Background", value: detail?.background, wide: true },
        { label: "Scope of Work", value: detail?.scopeOfWork, wide: true },
        { label: "Budget Status", value: detail?.budgetStatus },
        { label: "Commencement Date", value: displayDate(detail?.commencementDate) },
        { label: "Completion Date", value: displayDate(detail?.completionDate) },
        { label: "Region", value: detail?.region },
        { label: "Work Order Contract Category", value: detail?.category },
        { label: "Sub Region", value: detail?.subRegion },
        { label: "Procurement Approach", value: approachLabel(detail?.procurementApproach) },
        { label: "Vendor", value: detail?.vendor },
      ]),

      title("SOR Details"),
      table(
        ["SOR ID", "SOR Item", "Description", "UOM", "Quantity", "Rate", "Amount"],
        ["12%", "18%", "28%", "8%", "10%", "10%", "14%"],
        sor.map((row) => [row.sorId, row.itemName, row.description, row.uom || "-", num(row.quantity), num(row.rate), num(row.amount)]),
        "No SOR items found"
      ),
      {
        columns: [
          { text: "Total Amount", style: "fieldLabel", alignment: "right", width: "*" },
          { text: total.toLocaleString(), style: "fieldValue", width: "14%", margin: [4, 0, 0, 0] },
        ],
        marginTop: 8,
      },

      title("Project Documents"),
      ...(documents.length
        ? documents.map((doc) => {
            const cleanUrl = doc.url || "";
            return {
              columns: [
                { image: pdfLogo, fit: [30, 20], width: 30 },
                { text: doc.name, style: "fieldValue", ...(cleanUrl && { link: cleanUrl }) },
              ],
              marginBottom: 12,
            };
          })
        : [{ text: "-", style: "fieldValue" }]),

      title("Additional Document Link"),
      ...(links.length ? links : [{}]).map((link: any) => {
        const url = link.documentLink ?? link.url;
        return {
          columns: [
            { text: dash(link.documentName ?? link.name), fontSize: 11, color: "#29256E" },
            url
              ? { text: url, link: url, fontSize: 11, color: "#29256E", decoration: "underline" }
              : { text: "-", fontSize: 11, color: "#29256E" },
          ],
          marginBottom: 12,
        };
      }),

      title("Reviewers Details"),
      table(
        ["Name", "Email Address", "Approving Order", "Status", "Reviewed At"],
        ["20%", "30%", "14%", "16%", "20%"],
        reviewers.map((row) => [row.reviewerName, row.email, row.routingOrder, status(row.status), getFormattedDate(row.reviewedAt)]),
        "No Reviewers Details Found"
      ),

      title("Approvers Details"),
      field("Approval Authority", authorityLabelOf(detail, routing)),
      table(
        ["Designation", "Name", "Email Address", "Approving Order", "Status", "Reviewed At"],
        ["16%", "17%", "25%", "12%", "13%", "17%"],
        approvers.map((row) => [
          row.designation,
          row.reviewerName,
          row.email,
          row.routingOrder,
          status(row.status),
          getFormattedDate(row.reviewedAt),
        ]),
        "No Approvers Details Found"
      ),

      title("Comments"),
      table(
        ["Name", "Email", "Doc Name", "Version", "Comments", "Commented At"],
        ["14%", "20%", "16%", "10%", "22%", "18%"],
        comments.map(commentFields).map((row) => [
          row.name,
          row.email,
          row.documentName ?? " ",
          row.version ? formatDocVersion(row.version) : " ",
          row.comment ?? " ",
          getFormattedDate(row.commentedOn),
        ]),
        "No comments found"
      ),

      title("Work Order Versions"),
      table(
        ["Version", "Last Updated"],
        ["30%", "70%"],
        versionRows,
        "No versions found"
      ),

      title("Logs"),
      table(
        ["Action", "Details", "Performed By", "Performed At"],
        ["20%", "40%", "20%", "20%"],
        logs.map((row) => {
          let detailText = row.detail ?? "";
          try {
            detailText = decodeURIComponent(detailText);
            detailText = detailText.replace(/\b(\d+)\.(\d+)\b/g, (_: string, major: string, minor: string) => {
              const version = (Number(major) - 1) * 10 + Number(minor);
              return `V${version}`;
            });
          } catch {
            /* plain text that merely contains a "%" */
          }
          return [row.action, detailText, row.actionPerformedBy, getFormattedDate(row.actionPerformedOn)];
        }),
        "No logs found"
      ),
    ],
    /* e-POD's styles (generate-pdf.ts), unchanged. */
    styles: {
      pageTitle: { fontSize: 14, color: "#29256E", bold: true },
      fieldLabel: { fontSize: 12, color: "#29256E", marginBottom: 4 },
      fieldValue: { fontSize: 11, color: "#000" },
      tableHeader: { margin: 4, fontSize: 12, color: "#29256E" },
      tableContent: { margin: 4, fontSize: 11 },
    },
  };
};

/** Builds the document and hands back the pdfmake instance to print/save/view. */
export const createWorkOrderPdf = (detail: any, routing: any[], extras?: WorkOrderPdfExtras) =>
  pdfMake.createPdf(workOrderPdf(detail, routing, extras) as any);
